/**
 * Portal Credit Audit API Client
 *
 * Handles listing credit audit records for billing management.
 * Available to portal_system_admin, portal_director, and portal_manager.
 * Managers can only see records for students in their campus.
 */

// API Gateway URL
const API_BASE_URL = 'https://jrgfnay5w2.execute-api.ap-southeast-2.amazonaws.com/v1';

/**
 * Get the authentication token from the user's session.
 */
const getAuthToken = () => {
  const oidcStorage = sessionStorage.getItem('oidc.user:https://cognito-idp.ap-southeast-2.amazonaws.com/ap-southeast-2_3n9j6gDzL:2c6na652k2lkcsaa1ol8m5chp4');
  if (oidcStorage) {
    try {
      const oidcData = JSON.parse(oidcStorage);
      return oidcData.id_token || oidcData.access_token;
    } catch (e) {
      console.error('Error parsing OIDC storage:', e);
    }
  }
  return null;
};

/**
 * Make an authenticated API request to credit audit endpoint.
 */
const apiRequest = async (operation, data = {}) => {
  const token = getAuthToken();

  if (!token) {
    throw new Error('Not authenticated. Please log in again.');
  }

  const response = await fetch(`${API_BASE_URL}/credit-audit-admin`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      operation,
      data
    })
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || `Request failed with status ${response.status}`);
  }

  return result;
};

/**
 * List credit audit records with optional filters.
 * Available to portal_system_admin, portal_director, and portal_manager.
 * Managers can only see records for students in their campus.
 *
 * @param {Object} filters - Optional filters
 * @param {string} filters.dateFrom - Start date (YYYY-MM-DD)
 * @param {string} filters.dateTo - End date (YYYY-MM-DD)
 * @param {string} filters.studentSearch - Search term for student name
 * @param {string} filters.descriptionSearch - Search term for description
 * @param {string} filters.operatorSearch - Search term for operator name
 * @param {string} filters.campusId - Filter by campus ID
 * @param {string} filters.transferType - Filter by type: 'normal', 'from_student', 'to_student', 'all'
 * @returns {Object} Object with audits array and optional requesterCampusId
 */
export const listCreditAudits = async (filters = {}) => {
  const result = await apiRequest('list', { filters });
  return {
    audits: result.audits || [],
    requesterCampusId: result.requesterCampusId
  };
};

/**
 * Get presigned URL for downloading an attachment.
 *
 * @param {string} auditId - The audit record ID
 * @returns {Object} Object with url, isImage, and filename
 */
export const getAttachmentUrl = async (auditId) => {
  const result = await apiRequest('getAttachmentUrl', { auditId });
  return {
    url: result.url,
    isImage: result.isImage,
    filename: result.filename
  };
};

/**
 * Format amount for display (e.g., +$100.00 or -$50.00)
 *
 * @param {number} amount - The amount to format
 * @param {boolean} showSign - Whether to show + sign for positive numbers
 * @returns {string} Formatted amount string
 */
export const formatAmount = (amount, showSign = true) => {
  const num = parseFloat(amount) || 0;
  const sign = showSign && num > 0 ? '+' : '';
  return `${sign}$${Math.abs(num).toFixed(2)}`;
};

/**
 * Get transfer type display label
 *
 * @param {string} type - The transfer type
 * @returns {string} Human-readable label
 */
export const getTransferTypeLabel = (type) => {
  const labels = {
    'normal': 'External Transaction',
    'from_student': 'Balance to Course',
    'to_student': 'Course to Balance',
    'term_settlement': 'Term Settlement'
  };
  return labels[type] || type;
};

/**
 * Format date for display
 *
 * @param {string} isoDate - ISO date string
 * @returns {string} Formatted date string
 */
export const formatDate = (isoDate) => {
  if (!isoDate) return '-';
  try {
    const date = new Date(isoDate);
    return date.toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (e) {
    return isoDate;
  }
};

/**
 * Transfer type options for filter dropdown
 */
export const TRANSFER_TYPE_OPTIONS = [
  { value: 'all', label: 'All Types' },
  { value: 'normal', label: 'External Transactions' },
  { value: 'from_student', label: 'Balance to Course' },
  { value: 'to_student', label: 'Course to Balance' },
  { value: 'term_settlement', label: 'Term Settlement' }
];

const creditAuditPortalApi = {
  listCreditAudits,
  getAttachmentUrl,
  formatAmount,
  getTransferTypeLabel,
  formatDate,
  TRANSFER_TYPE_OPTIONS
};

export default creditAuditPortalApi;
